import { Injectable, OnDestroy } from '@angular/core';
import { finalize, of, takeUntil, timer, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CategoriesModalComponent } from '@form-field-module/modal-windows/categories-modal/categories-modal.component';
import { AddTagsModalComponent } from '@form-field-module/modal-windows/add-tags-modal/add-tags-modal.component';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';

@Injectable({ providedIn: 'root' })
export class AssignCategoryAndTagsService implements OnDestroy {
    isModalWindowOpen: boolean;

    private unsubscriber = new Subject<void>();

    constructor(
        private bsModalService: BsModalService,
        private tagService: TagService,
        private commonDataService: CommonDataService,
        private translateService: TranslateService,
    ) {}

    assignCategoryAndTags<T>(
        payload: T,
        isShowBackdrop = false,
    ): Subject<T & { categoryId?: number; tags?: string[] }> {
        const result = new Subject<T & { categoryId?: number; tags?: string[] }>();
        if (
            (payload as Record<string, any>).status === TicketStatus.completed &&
            !(payload as Record<string, any>).categoryId &&
            this.commonDataService.customerSettings?.isTicketCategoryRequired
        ) {
            const initialState = {
                isPrivate: false,
                isDisableRemoving: true,
                isIgnoreUnselected: true,
                onChangeCategory: (selectedCategory: { id: any }) => {
                    this.assignTags<T & { categoryId: number }>(
                        {
                            ...payload,
                            categoryId: selectedCategory.id,
                        },
                        isShowBackdrop,
                    )
                        .pipe(
                            takeUntil(this.unsubscriber),
                            finalize(() => {
                                result.complete();
                            }),
                        )
                        .subscribe((data) => {
                            result.next(data);
                        });

                    if (
                        (payload as Record<string, any>).status !== TicketStatus.completed ||
                        !this.commonDataService.customerSettings?.isTicketTagRequiredForClosing
                    ) {
                        this.isModalWindowOpen = false;
                    }
                },
                onClose: () => {
                    result.complete();
                    this.isModalWindowOpen = false;
                },
                alertMessage: this.translateService.instant('Ticket must have category to be closed.'),
            };

            this.bsModalService.show(CategoriesModalComponent, {
                initialState,
                ignoreBackdropClick: true,
                backdrop: isShowBackdrop,
            });
            this.isModalWindowOpen = true;
        } else {
            this.assignTags<T>(payload, isShowBackdrop)
                .pipe(
                    takeUntil(this.unsubscriber),
                    finalize(() => {
                        result.complete();
                    }),
                )
                .subscribe((data) => {
                    result.next(data);
                });
        }

        return result;
    }

    private assignTags<T>(payload: T, isShowBackdrop: boolean): Subject<T & { tags?: string[] }> {
        const result = new Subject<T & { tags?: string[] }>();

        if (
            (payload as Record<string, any>).status === TicketStatus.completed &&
            this.commonDataService.customerSettings?.isTicketTagRequiredForClosing
        ) {
            const tagsObservable = (payload as Record<string, any>).categoryId
                ? this.tagService.getTagsByCategory((payload as Record<string, any>).categoryId)
                : of([]);
            let categoryTags = [];

            const showTagsModal = (tags: any[]) => {
                const initialState = {
                    tags: [...this.tagService.globalTags, ...tags],
                    confirmCallback: (selectedTags) => {
                        result.next({
                            ...payload,
                            tags: selectedTags.map((item: { text: any }) => item.text),
                        });
                        this.isModalWindowOpen = false;
                    },
                    onClose: () => {
                        result.complete();
                        this.isModalWindowOpen = false;
                    },
                    alertMessage: this.translateService.instant('Ticket must have tags to be closed.'),
                    isRequired: true,
                };

                this.bsModalService.show(AddTagsModalComponent, {
                    initialState,
                    ignoreBackdropClick: true,
                    backdrop: isShowBackdrop,
                });
                this.isModalWindowOpen = true;
            };

            tagsObservable.pipe(takeUntil(this.unsubscriber)).subscribe({
                next: (tags) => {
                    categoryTags = tags;
                    showTagsModal(categoryTags);
                },
                error: () => {
                    showTagsModal(categoryTags);
                },
            });
        } else {
            timer(0).subscribe(() => result.next(payload));
        }

        return result;
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
